import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import type { TerminalInfo } from "@racler/protocol";
import { useRelay } from "../lib/relayContext.tsx";
import { IconButton } from "./IconButton.tsx";
import { TerminalView } from "./TerminalView.tsx";
import { ProblemsPanel, problemCounts } from "./ProblemsPanel.tsx";
import { useProjectLspState } from "../lib/lsp/useProjectLsp.ts";

const PLACEHOLDER_TABS = ["Git", "Tests", "Preview"] as const;
type Tab = "Terminal" | "Problems" | (typeof PLACEHOLDER_TABS)[number];

/** Lower panel (only below the editor). Terminal and Problems (M6.1) are functional so far. */
export function BottomPanel({
  machineId,
  projectId,
  onHide,
}: {
  machineId: string;
  projectId: string;
  onHide?: () => void;
}) {
  const relay = useRelay();
  const { problems } = useProjectLspState();
  const counts = problemCounts(problems);
  const [tab, setTab] = useState<Tab>("Terminal");
  const [terminals, setTerminals] = useState<TerminalInfo[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-discover terminals that are still running on the machine (e.g. after a page reload).
  useEffect(() => {
    relay
      .machine(machineId, "terminal/terminal.list", {})
      .then((r) => {
        const mine = r.terminals.filter((t) => t.projectId === projectId && !t.exited);
        setTerminals(mine);
        setActive((a) => a ?? mine[0]?.terminalId ?? null);
      })
      .catch((e: unknown) => setError((e as Error).message));
  }, [relay, machineId, projectId]);

  const create = useCallback(() => {
    setError(null);
    relay
      .machine(machineId, "terminal/terminal.create", { projectId, cols: 100, rows: 24 })
      .then((r) => {
        setTerminals((ts) => [...ts, r.terminal]);
        setActive(r.terminal.terminalId);
        setTab("Terminal");
      })
      .catch((e: unknown) => setError((e as Error).message));
  }, [relay, machineId, projectId]);

  const kill = (terminalId: string) => {
    relay.machine(machineId, "terminal/terminal.kill", { terminalId }).catch(() => undefined);
    setTerminals((ts) => {
      const rest = ts.filter((t) => t.terminalId !== terminalId);
      setActive((a) => (a === terminalId ? (rest[0]?.terminalId ?? null) : a));
      return rest;
    });
  };

  return (
    <div className="bottom-panel" data-testid="bottom-panel">
      <div className="bottom-tabs">
        {(["Terminal", "Problems", ...PLACEHOLDER_TABS] as Tab[]).map((t) => (
          <button
            key={t}
            className={`btab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
            data-testid={`tab-panel-${t}`}
          >
            {t}
            {t === "Problems" && counts.errors + counts.warnings > 0 && (
              <span className="muted" data-testid="problems-count">
                {" "}
                {counts.errors > 0 ? `${counts.errors}✕` : ""}
                {counts.errors > 0 && counts.warnings > 0 ? " " : ""}
                {counts.warnings > 0 ? `${counts.warnings}⚠` : ""}
              </span>
            )}
          </button>
        ))}
        <span className="spacer" />
        {tab === "Terminal" && (
          <>
            {terminals.map((t, i) => (
              <span key={t.terminalId} className={`term-chip ${active === t.terminalId ? "active" : ""}`}>
                <button className="chip-main" onClick={() => setActive(t.terminalId)}>
                  {i + 1}: {t.title}
                  {t.exited ? " (exited)" : ""}
                </button>
                <button className="chip-x" aria-label="Kill terminal" onClick={() => kill(t.terminalId)}>
                  <X size={12} aria-hidden />
                </button>
              </span>
            ))}
            <IconButton icon={Plus} label="New terminal" onClick={create} testId="new-terminal" />
          </>
        )}
        {onHide && (
          <IconButton
            icon={ChevronDown}
            label="Hide panel (Ctrl/Cmd+J)"
            onClick={onHide}
            testId="hide-bottom"
          />
        )}
      </div>
      <div className="bottom-body">
        {tab === "Terminal" && (
          <>
            {error && <div className="error banner">{error}</div>}
            {terminals.length === 0 && (
              <div className="center muted">
                <button onClick={create}>Open a terminal in this Project</button>
              </div>
            )}
            {terminals.map((t) => (
              <TerminalView
                key={t.terminalId}
                machineId={machineId}
                terminalId={t.terminalId}
                visible={active === t.terminalId}
                onExit={(code) =>
                  setTerminals((ts) =>
                    ts.map((x) =>
                      x.terminalId === t.terminalId ? { ...x, exited: true, exitCode: code } : x,
                    ),
                  )
                }
              />
            ))}
          </>
        )}
        {tab === "Problems" && <ProblemsPanel />}
        {tab !== "Terminal" && tab !== "Problems" && <div className="center muted">{tab} is planned for a later milestone.</div>}
      </div>
    </div>
  );
}
